const IG_MIN_SIDE = 320;
const IG_MAX_SIDE = 1440;
const JPEG_QUALITY = 0.95;

// Raw fetch: the image lives on the CDN, outside the pharmacy API namespace
async function fetchImageBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { mode: 'cors', cache: 'reload' });

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }

  return response.blob();
}

export async function prepareInstagramImageFromBlob(
  source: Blob
): Promise<File | null> {
  const image = await createImageBitmap(source, {
    imageOrientation: 'from-image',
  });
  const { width, height } = image;
  const side = Math.min(width, height);

  if (side < IG_MIN_SIDE) {
    console.warn('[ig-crop] not croppable: smallest side below minimum', {
      side,
      min: IG_MIN_SIDE,
    });

    return null;
  }

  const targetSide = Math.min(side, IG_MAX_SIDE);
  const canvas = document.createElement('canvas');

  canvas.width = targetSide;
  canvas.height = targetSide;

  const context = canvas.getContext('2d');

  if (!context) {
    console.error('[ig-crop] canvas 2d context unavailable');

    return null;
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, targetSide, targetSide);

  const sourceX = (width - side) / 2;
  const sourceY = (height - side) / 2;

  context.drawImage(
    image,
    sourceX,
    sourceY,
    side,
    side,
    0,
    0,
    targetSide,
    targetSide
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );

  if (!blob) {
    console.error('[ig-crop] toBlob returned null');

    return null;
  }

  return new File([blob], 'instagram.jpg', { type: 'image/jpeg' });
}

export async function prepareInstagramImageFromUrl(
  url: string
): Promise<File | null> {
  const blob = await fetchImageBlob(url);

  return prepareInstagramImageFromBlob(blob);
}
